import { useCallback, useEffect, useState, useRef } from "react";
import {
  ActionIcon,
  Box,
  Group,
  Paper,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconMicrophone, IconMicrophoneOff, IconPlayerStop } from "@tabler/icons-react";

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: SpeechRecognitionEvent) => void;
  onerror: (event: SpeechRecognitionErrorEvent) => void;
  onend: () => void;
  onstart: () => void;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

interface VoiceInputProps {
  onTranscript: (text: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function VoiceInput({
  onTranscript,
  placeholder = "Tap to speak...",
  disabled = false,
}: VoiceInputProps) {
  const theme = useMantineTheme();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  // Check if Speech Recognition is supported
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result && result[0]) {
          const transcriptText = result[0].transcript;
          if (result.isFinal) {
            final += transcriptText;
          } else {
            interim += transcriptText;
          }
        }
      }

      if (final) {
        setTranscript((prev) => {
          const newTranscript = prev + (prev ? " " : "") + final;
          return newTranscript;
        });
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("Speech recognition error:", event.error);

      if (event.error === "not-allowed") {
        notifications.show({
          title: "Microphone access denied",
          message: "Please allow microphone access to use voice input",
          color: "red",
        });
      } else if (event.error !== "aborted") {
        notifications.show({
          title: "Voice input error",
          message: "Something went wrong. Please try again.",
          color: "red",
        });
      }

      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Submit transcript when stopping
  const handleStop = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
    }

    // Combine final and interim transcript
    const fullTranscript = transcript + (interimTranscript ? " " + interimTranscript : "");

    if (fullTranscript.trim()) {
      onTranscript(fullTranscript.trim());
    }

    setTranscript("");
    setInterimTranscript("");
  }, [isListening, transcript, interimTranscript, onTranscript]);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      handleStop();
    } else {
      setTranscript("");
      setInterimTranscript("");
      try {
        recognitionRef.current.start();
      } catch (error) {
        console.error("Failed to start recognition:", error);
      }
    }
  }, [isListening, handleStop]);

  if (!isSupported) {
    return (
      <Tooltip label="Voice input not supported in this browser">
        <ActionIcon
          size="lg"
          variant="light"
          color="gray"
          disabled
        >
          <IconMicrophoneOff size={20} />
        </ActionIcon>
      </Tooltip>
    );
  }

  const displayText = transcript + (interimTranscript ? " " + interimTranscript : "");

  return (
    <Group gap="sm" align="flex-start">
      <Tooltip label={isListening ? "Stop recording" : "Start voice input"}>
        <ActionIcon
          size="xl"
          variant={isListening ? "filled" : "light"}
          color={isListening ? "red" : "blue"}
          onClick={toggleListening}
          disabled={disabled}
          style={{
            animation: isListening ? "pulse 1.5s ease-in-out infinite" : "none",
          }}
        >
          {isListening ? <IconPlayerStop size={24} /> : <IconMicrophone size={24} />}
        </ActionIcon>
      </Tooltip>

      {isListening && (
        <Paper
          p="sm"
          withBorder
          style={{
            flex: 1,
            minHeight: 60,
            background: theme.colors.blue[0],
            borderColor: theme.colors.blue[3],
          }}
        >
          {displayText ? (
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {displayText}
              <Box
                component="span"
                style={{
                  display: "inline-block",
                  width: 2,
                  height: "1em",
                  backgroundColor: theme.colors.blue[6],
                  marginLeft: 2,
                  animation: "blink 1s infinite",
                }}
              />
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              {placeholder}
            </Text>
          )}
        </Paper>
      )}

      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0; }
          }
        `}
      </style>
    </Group>
  );
}

export default VoiceInput;
